import {
  canonicalTwitchLogin,
  type LiveBreak,
  type LiveBreakHost,
  type LiveBreakMyQueuedEntry,
  type LiveBreakRevealPhase,
  type LiveBreakSponsoredStream,
  type ResolvedThemeSpec,
  type TwitchChannel,
} from "@loot-os/api-contract";

import { cardStyles } from "../../core/Card.styles.js";
import { liveBreakHostName, pendingQueuedCount } from "./liveBreakState.js";

/**
 * The live-breaks widget's rows as plain data, and the rules that build them from the `/v1` reads
 * (boxed's `useLiveStreamCandidates`, `usePromotedStreams`, `resolvePromotedSlot`, `pickerEntries`,
 * `useLiveBreakQueueSlot` and `useLiveBreakHostSlot`), so the storefront and the theme preview run
 * the same ones. `LiveBreaksWidget` draws what it is given; which rows the caller gives it is the
 * caller's (boxed's `resolveVisibleWidgetSlots`):
 * - none on a live break's own page (picture-in-picture still plays there);
 * - no stream row while picture-in-picture plays, or after Hide widget until it opens again;
 * - the stream row is `resolveWidgetStream` over the sponsored rows less any live break a Return
 *   row already shows (the pinned row shows even then);
 * - the hosting row and the queue row both go in; the widget shows the queue row only without one.
 */

/**
 * One Twitch stream as the widget's stream row shows it and picture-in-picture plays it (boxed's
 * `WatchableStream` and `LiveStreamNotification`): a live break's host on their channel, or a
 * promoted stream, live on Twitch with no live break. Two are the same stream when `channel` matches.
 */
export interface LiveBreakWidgetStream {
  /**
   * Unique across both kinds (the live break's id, else `promoted:{channel}`): the picker's value,
   * and the key the shopper's collapsed setting is kept under.
   */
  key: string;
  /** The canonical Twitch login. */
  channel: string;
  /** The name shown: a live break's host; a promoted stream's staff name, else Twitch's, else the login. */
  name: string;
  /** Twitch's avatar for the channel; the name's initials show without one. */
  avatarSrc: string | null;
  /** Lower shows first: the sponsored stream's priority (0 for a pinned live break). */
  priority: number;
  /** The live break it belongs to, or null for a promoted stream (it has no Join). */
  liveBreak: {
    id: string;
    /** Its page. */
    href: string;
    /** Its stage; null while a pinned live break loads, or in the picker's Recent group. */
    phase: LiveBreakRevealPhase | null;
  } | null;
}

/** A Return row: back to a live break the shopper hosts or waits in, by its host. */
export interface LiveBreakWidgetReturn {
  liveBreakId: string;
  /** Its page. */
  href: string;
  hostName: string;
  /** The host's Twitch avatar; their initials show without one. */
  hostAvatarSrc: string | null;
}

/** The hosting Return row: the shopper's own open live break and how many wait in it. */
export interface LiveBreakWidgetHosting extends LiveBreakWidgetReturn {
  /** Entries waiting, the one on stage left out. */
  queuedCount: number;
}

/** The queue Return row: the shopper's soonest place in any live break's queue. */
export interface LiveBreakWidgetQueue extends LiveBreakWidgetReturn {
  /** 1-based, Up Next is 1. */
  position: number;
}

/** The stream picker's two groups (boxed's `pickerEntries`). */
export interface LiveBreakWidgetPickerSections {
  /** Sponsored live breaks and promoted streams, by priority. */
  sponsored: readonly LiveBreakWidgetStream[];
  /** Live breaks watched in picture-in-picture lately and still open, newest first, less any listed above. */
  recent: readonly LiveBreakWidgetStream[];
}

/** A promoted stream's key, kept apart from live break ids. */
export function promotedStreamKey(channel: string): string {
  return `promoted:${channel}`;
}

/** Lowest priority first, ties by key (boxed's `compareByPriority`). */
export function compareWidgetStreams(a: LiveBreakWidgetStream, b: LiveBreakWidgetStream): number {
  if (a.priority !== b.priority) return a.priority - b.priority;
  return a.key < b.key ? -1 : a.key > b.key ? 1 : 0;
}

function channelIndex(channels: readonly TwitchChannel[]): ReadonlyMap<string, TwitchChannel> {
  return new Map(channels.map((channel) => [canonicalTwitchLogin(channel.login), channel]));
}

/**
 * A live break as a stream row, for the sponsored rows and the pinned one (picture-in-picture's
 * last live break); null when it shows no channel. A house live break goes by the store's name.
 */
export function liveBreakWidgetStream(
  liveBreak: Pick<LiveBreak, "id" | "host" | "twitchChannel" | "twitchStatus" | "revealPhase">,
  {
    href,
    houseName,
    priority = 0,
    channel,
  }: {
    href: string;
    houseName: string;
    priority?: number;
    /** The channel as `GET /v1/twitch/channels` read it, for a fresher avatar than the live break's. */
    channel?: TwitchChannel | null;
  },
): LiveBreakWidgetStream | null {
  if (!liveBreak.twitchChannel) return null;
  return {
    key: liveBreak.id,
    channel: canonicalTwitchLogin(liveBreak.twitchChannel),
    name: liveBreakHostName(liveBreak, houseName),
    avatarSrc: channel?.avatarUrl ?? liveBreak.twitchStatus?.avatarUrl ?? null,
    priority,
    liveBreak: { id: liveBreak.id, href, phase: liveBreak.revealPhase },
  };
}

/**
 * The sponsored rows (boxed's `useLiveStreamCandidates`): every open live break, not Draining,
 * its host present, on a sponsored stream's channel, by that stream's priority. The shopper's own
 * live breaks are left out: their hosting Return row stands for them.
 */
export function sponsoredLiveBreakStreams({
  sponsoredStreams,
  liveBreaks,
  channels = [],
  viewerEndUserId,
  href,
  houseName,
}: {
  /** `GET /v1/live-breaks/sponsored-streams`. */
  sponsoredStreams: readonly LiveBreakSponsoredStream[];
  /** `GET /v1/live-breaks/by-channel` for the sponsored logins. */
  liveBreaks: readonly LiveBreak[];
  /** `GET /v1/twitch/channels` for the same logins. */
  channels?: readonly TwitchChannel[];
  /** The signed-in shopper's end-user id; null when signed out. */
  viewerEndUserId: string | null;
  /** A live break's page. */
  href: (liveBreakId: string) => string;
  houseName: string;
}): LiveBreakWidgetStream[] {
  const priorities = new Map<string, number>();
  for (const stream of sponsoredStreams) {
    const login = canonicalTwitchLogin(stream.twitchLogin);
    priorities.set(login, Math.min(priorities.get(login) ?? stream.priority, stream.priority));
  }
  const byLogin = channelIndex(channels);
  return liveBreaks
    .flatMap((liveBreak) => {
      if (liveBreak.status !== "open" || liveBreak.isDraining || !liveBreak.isHostPresent) {
        return [];
      }
      if (!liveBreak.twitchChannel) return [];
      if (viewerEndUserId !== null && liveBreak.host?.endUserId === viewerEndUserId) return [];
      const login = canonicalTwitchLogin(liveBreak.twitchChannel);
      const priority = priorities.get(login);
      if (priority === undefined) return [];
      const row = liveBreakWidgetStream(liveBreak, {
        href: href(liveBreak.id),
        houseName,
        priority,
        channel: byLogin.get(login),
      });
      return row ? [row] : [];
    })
    .sort(compareWidgetStreams);
}

/**
 * The promoted streams (boxed's `usePromotedStreams`): a sponsored stream live on Twitch with no
 * sponsored live break, by priority, the shopper's own linked channel left out.
 */
export function promotedWidgetStreams({
  sponsoredStreams,
  channels,
  hostingChannels,
  ownLogin = null,
}: {
  sponsoredStreams: readonly LiveBreakSponsoredStream[];
  /** `GET /v1/twitch/channels` for every sponsored login. */
  channels: readonly TwitchChannel[];
  /** The channels the sponsored rows already show (`sponsoredLiveBreakStreams`'). */
  hostingChannels: Iterable<string>;
  /** The shopper's linked Twitch login, if any. */
  ownLogin?: string | null;
}): LiveBreakWidgetStream[] {
  const byLogin = channelIndex(channels);
  const hosting = new Set([...hostingChannels].map(canonicalTwitchLogin));
  const own = ownLogin ? canonicalTwitchLogin(ownLogin) : null;
  const seen = new Set<string>();
  return sponsoredStreams
    .flatMap((stream) => {
      const login = canonicalTwitchLogin(stream.twitchLogin);
      if (seen.has(login) || hosting.has(login) || login === own) return [];
      seen.add(login);
      const channel = byLogin.get(login);
      if (!channel?.isLive) return [];
      return [
        {
          key: promotedStreamKey(login),
          channel: login,
          name: stream.displayName || channel.displayName || login,
          avatarSrc: channel.avatarUrl,
          priority: stream.priority,
          liveBreak: null,
        },
      ];
    })
    .sort(compareWidgetStreams);
}

/**
 * The stream row (boxed's `resolvePromotedSlot`): the pinned live break, then a promoted stream the
 * shopper picked while it is still live, then the top sponsored row, then the top promoted stream.
 */
export function resolveWidgetStream({
  pinned,
  pickedChannel,
  sponsored,
  promoted,
}: {
  pinned: LiveBreakWidgetStream | null;
  /** The promoted stream the shopper last picked; null once they picked a live break. */
  pickedChannel: string | null;
  /** The sponsored rows, less any live break a Return row shows. */
  sponsored: readonly LiveBreakWidgetStream[];
  promoted: readonly LiveBreakWidgetStream[];
}): LiveBreakWidgetStream | null {
  if (pinned) return pinned;
  const picked = promoted.find((stream) => stream.channel === pickedChannel);
  return picked ?? sponsored[0] ?? promoted[0] ?? null;
}

/**
 * The picker's groups (boxed's `pickerEntries`): sponsored rows and promoted streams by priority,
 * then the recent live breaks not already listed by live break or by channel.
 */
export function widgetPickerSections({
  sponsored,
  promoted,
  recent,
}: {
  sponsored: readonly LiveBreakWidgetStream[];
  promoted: readonly LiveBreakWidgetStream[];
  /** Live breaks watched in picture-in-picture this session and still open, newest first. */
  recent: readonly LiveBreakWidgetStream[];
}): LiveBreakWidgetPickerSections {
  const listed = [...sponsored, ...promoted].sort(compareWidgetStreams);
  const keys = new Set(listed.map((stream) => stream.key));
  const channels = new Set(listed.map((stream) => stream.channel));
  return {
    sponsored: listed,
    recent: recent.filter(
      (stream) =>
        stream.liveBreak !== null && !keys.has(stream.key) && !channels.has(stream.channel),
    ),
  };
}

/** Whether the picker has anything to choose: more than one stream across its groups. */
export function hasStreamChoices(sections: LiveBreakWidgetPickerSections | null | undefined) {
  return !!sections && sections.sponsored.length + sections.recent.length > 1;
}

/**
 * The queue Return row (boxed's `useLiveBreakQueueSlot`): the shopper's soonest place across the
 * open live breaks they wait in; null with none waiting.
 */
export function queueReturnRow({
  entries,
  href,
  houseName,
}: {
  /** `GET /v1/me/live-breaks/entries`. */
  entries: readonly LiveBreakMyQueuedEntry[];
  href: (liveBreakId: string) => string;
  houseName: string;
}): LiveBreakWidgetQueue | null {
  let best: (LiveBreakMyQueuedEntry & { position: number }) | null = null;
  for (const entry of entries) {
    if (entry.position === null || entry.liveBreak.status !== "open") continue;
    if (best === null || entry.position < best.position)
      best = { ...entry, position: entry.position };
  }
  if (best === null) return null;
  return {
    liveBreakId: best.liveBreak.id,
    href: href(best.liveBreak.id),
    hostName: liveBreakHostName(best.liveBreak, houseName),
    hostAvatarSrc: null,
    position: best.position,
  };
}

/**
 * The hosting Return row (boxed's `useLiveBreakHostSlot`): the shopper's newest open live break and
 * the entries waiting in it; null when they host none.
 */
export function hostingReturnRow({
  host,
  href,
  houseName,
}: {
  /** `GET /v1/me/live-break-host`; null when signed out. */
  host: Pick<LiveBreakHost, "openLiveBreaks"> | null;
  href: (liveBreakId: string) => string;
  houseName: string;
}): LiveBreakWidgetHosting | null {
  const liveBreak = host?.openLiveBreaks.find((open) => open.status === "open");
  if (!liveBreak) return null;
  return {
    liveBreakId: liveBreak.id,
    href: href(liveBreak.id),
    hostName: liveBreakHostName(liveBreak, houseName),
    hostAvatarSrc: liveBreak.twitchStatus?.avatarUrl ?? null,
    queuedCount: pendingQueuedCount(liveBreak),
  };
}

/**
 * The classes of the widget's floating surfaces (its rows, its pill, the picture-in-picture frame):
 * glass under glass chrome, as the store's chat is, else the card surface at `radius`, floating.
 */
export function widgetSurface(
  chrome: ResolvedThemeSpec["layout"]["chrome"],
  radius: "lg" | "xl" = "xl",
): string {
  return chrome === "glass"
    ? "border border-border-subtle bg-bg-canvas/85 shadow-float backdrop-blur-xl"
    : `${cardStyles({ radius })} shadow-float`;
}
